import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Package } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import SEOHead from '../components/seo/SEOHead';
import ScrambleText from '../components/effects/ScrambleText';
import TiltCard from '../components/effects/TiltCard';
import productsData from '../data/completeProductsData';
import { trackPageView } from '../utils/analytics';

const ProductsLandingPage = () => {
  useEffect(() => {
    trackPageView('Products Landing');
  }, []);

  const productCategories = [
    {
      ...productsData.laryngoStroboscopyProcessors,
      image: '/products/transparent/endostrobe-pl-spectar-web.png',
      color: 'from-[#00FFD1]/20 to-[#00FFD1]/5'
    },
    {
      ...productsData.surgeryProcessors,
      image: '/products/transparent/csm-matrix-pl-spectar-web-3bd7424485.png',
      color: 'from-[#00FFD1]/10 to-transparent'
    },
    {
      ...productsData.officeMobileProcessors,
      image: '/products/transparent/matrix-e-spectar-web.png',
      color: 'from-[#00FFD1]/10 to-transparent'
    },
    {
      ...productsData.cameraHeads,
      image: '/products/transparent/spectar-kamerakopf-4k-uhd-web.png',
      color: 'from-[#00FFD1]/10 to-transparent'
    },
    {
      ...productsData.flexibleEndoscopes,
      image: '/products/transparent/spectar-video-nasopharyngoskop-sterrad-web.png',
      color: 'from-[#00FFD1]/10 to-transparent'
    },
    {
      ...productsData.fibreOpticEndoscopes,
      image: '/products/transparent/faser-nasopharyngoskop-web.png',
      color: 'from-[#00FFD1]/10 to-transparent'
    },
    {
      ...productsData.compactCameras,
      image: '/products/transparent/veezar-kompaktkamera-web.png',
      color: 'from-[#00FFD1]/10 to-transparent'
    },
    {
      ...productsData.rigidEndoscopes,
      image: '/products/transparent/zoom-laryngoskop-10-mm-70-web.png',
      color: 'from-[#00FFD1]/10 to-transparent'
    },
    {
      ...productsData.illuminationSystems,
      image: '/products/transparent/csm-matrix-led-duo-01-3ac270249d.png',
      color: 'from-[#00FFD1]/10 to-transparent'
    },
    {
      ...productsData.documentationSystems,
      image: '/products/transparent/matrix-ds6-web.png',
      color: 'from-[#00FFD1]/10 to-transparent'
    },
    {
      ...productsData.medicalMonitors,
      image: '/products/transparent/xion-monitore-web.png',
      color: 'from-[#00FFD1]/10 to-transparent'
    },
    {
      ...productsData.softwareSystems,
      image: '/products/transparent/divas-2-8-web.png',
      color: 'from-[#00FFD1]/10 to-transparent'
    }
  ];

  return (
    <>
      <SEOHead
        title="Products - XION Medical Philippines"
        description="Complete range of endoscopy systems, camera processors, endoscopes, and instruments. Manufactured in Germany since 1991."
        path="/products"
      />
      
      <div className="min-h-screen bg-black">
        <Header />
        
        <main className="pt-32 pb-20 px-6">
          <div className="max-w-7xl mx-auto">
            {/* Hero Section */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center mb-16"
            >
              <div className="terminal-label text-[#00FFD1] mb-4">
                // Product Portfolio
              </div>
              <h1 className="text-5xl lg:text-6xl font-bold mb-6">
                <ScrambleText text="Products" className="text-white" />
              </h1>
              <p className="text-xl text-white/70 max-w-3xl mx-auto">
                Comprehensive endoscopy systems and components for minimally invasive diagnostics and therapy.
                Manufactured in Germany since 1991.
              </p>
            </motion.div>

            {/* Product Categories */}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
              {productCategories.map((category, idx) => (
                <motion.div
                  key={category.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.1 }}
                  className="group [perspective:1000px]"
                >
                  <TiltCard
                    data-testid={`product-category-card-${category.slug}`}
                    className="hud-card corner-brackets rounded-lg overflow-hidden h-full"
                  >
                    {/* Category Header with Image */}
                    <div className={`bg-gradient-to-br ${category.color} border-b border-white/10 p-6 flex items-center justify-center h-48 overflow-hidden`}>
                      {category.image ? (
                        <img 
                          src={category.image} 
                          alt={category.name}
                          className="w-full h-full object-contain tilt-lift"
                          onError={(e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<svg class="text-[#00FFD1]/60" width="64" height="64" fill="currentColor"><rect width="64" height="64" rx="8"/></svg>';
                          }}
                        />
                      ) : (
                        <Package size={64} className="text-[#00FFD1]/60 tilt-lift" />
                      )}
                    </div>

                    {/* Category Content */}
                    <div className="p-6">
                      <div className="inline-block px-3 py-1 bg-[#00FFD1]/10 border border-[#00FFD1]/30 rounded-full mb-3">
                        <span className="text-[#00FFD1] text-xs font-mono">{category.category}</span>
                      </div>
                      
                      <h2 className="text-2xl font-bold text-white mb-3 group-hover:text-[#00FFD1] transition-colors">
                        {category.name}
                      </h2>
                      
                      <p className="text-white/60 text-sm mb-4 line-clamp-3">
                        {category.description}
                      </p>

                      {/* Product Count */}
                      <div className="flex items-center justify-between mb-4 text-sm">
                        <span className="text-white/40">
                          {category.products.length} {category.products.length === 1 ? 'Product' : 'Products'}
                        </span>
                      </div>

                      <Link
                        to={`/products/${category.slug}`}
                        data-testid={`explore-category-${category.slug}`}
                        className="btn-secondary w-full justify-center group-hover:bg-[#00FFD1]/10"
                      >
                        Explore {category.category}
                        <ArrowRight size={16} />
                      </Link>
                    </div>
                  </TiltCard>
                </motion.div>
              ))}
            </div>

            {/* System Solutions CTA */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-gradient-to-br from-[#00FFD1]/10 to-transparent border-2 border-[#00FFD1]/50 rounded-lg p-12 text-center"
            >
              <h2 className="text-3xl font-bold text-white mb-4">
                Looking for Complete Systems?
              </h2>
              <p className="text-white/70 mb-6 max-w-2xl mx-auto">
                Explore our integrated system solutions designed for office endoscopy, 
                hospital applications, and specialized voice diagnostics.
              </p>
              <Link to="/system-solutions" className="btn-primary">
                View System Solutions
                <ArrowRight size={16} />
              </Link>
            </motion.div>

            {/* Distributor Banner */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-12 text-center"
            >
              <p className="text-white/60 mb-4">
                {productsData.distributor.statement}
              </p>
              <Link to="/contact-ph" className="btn-secondary inline-flex">
                Contact PH Distributor
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          </div>
        </main>
        
        <Footer />
      </div>
    </>
  );
};

export default ProductsLandingPage;
